'use client';
import { useState } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import ChevronRightIcon from '../Icons/ChevronRight';
import Spinner from '../Spinner';
import CheckIcon from '../Icons/Check';

import { cn } from '@/lib/utils';

import { IFooterDictionary } from '@/dictionaries/components/footer/types';
import Link from 'next/link';
import { postNewsletter } from '@/actions/newsletter';

interface Props {
  dict: IFooterDictionary;
  lang: Locale;
}

declare const window: Window & { dataLayer: Record<string, unknown>[] };

export default function NewsletterSubscription({ dict, lang }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const schema = yup.object().shape({
    email: yup
      .string()
      .email(dict.newsLetter.error.email)
      .required(dict.newsLetter.error.email),
    terms: yup
      .boolean()
      .oneOf([true], dict.newsLetter.error.terms)
      .required(dict.newsLetter.error.terms),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    trigger,
    clearErrors,
    setValue,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const onSubmit = handleSubmit(async (data) => {
    console.log(data);
    setIsLoading(true);
    if (data.terms) {
      postNewsletter(data.email, lang);
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'eventNewsletter',
        eventCategory: 'subscription_newletter',
        eventAction: 'form_newletter',
        eventLabel: window.location.href,
        language: lang,
      });
    }
    setTimeout(() => {
      setIsLoading(false);
      setSuccess(true);
    }, 5000);
  });

  return (
    <form
      onSubmit={onSubmit}
      className="mb-12 flex flex-col gap-2 desktop:w-[23%]">
      <label
        htmlFor="newsletter"
        className="font-semibold list-none uppercase pb-3 tracking-[2px] text-sm">
        {dict.newsLetter.title}
      </label>
      <div className="relative w-full md:w-80 lg:w-[30rem] desktop:w-full max-h-11 flex border border-footer-gray ">
        {isLoading && (
          <div className="absolute w-full h-full flex justify-center bg-white">
            <Spinner className="text-blue-500" />
          </div>
        )}
        <input
          {...register('email')}
          placeholder={dict.newsLetter.placeholder}
          className="w-[90%] px-4 py-2 text-base placeholder:text-sm placeholder:text-footer-gray bg-transparent focus:outline-none"
          type="text"
          autoComplete="off"
          id="newsletter"
        />
        <button
          id="newsletter-subscribe"
          aria-label="Subscribe to newsletter"
          type="submit"
          className="w-fit ml-auto py-2 text-gray-500 hover:text-yellow-400 h-full">
          {success ? (
            <CheckIcon className="w-7 h-7 fill-current text-success" />
          ) : (
            <ChevronRightIcon className="w-7 h-7 fill-current" />
          )}
        </button>
      </div>
      {errors.email && (
        <i className="text-error-primary font-averta-italic text-[13px]">
          {errors.email.message?.toString()}
        </i>
      )}
      <div className="flex items-center mt-1 gap-4 md:w-80 lg:w-[30rem] desktop:w-80">
        <input
          className={cn(
            'bg-transparent border border-footer-gray cursor-pointer w-4 h-4 appearance-none m-0 grid place-content-center [&]:before:content-[""] [&]:before:rounded-sm [&]:before:lg:rounded-none [&]:before:w-[10px] [&]:before:h-[10px] [&]:before:scale-0 [&]:checked:before:scale-100 [&]:before:bg-underline',
            {
              'border border-white': watch('terms') == true,
            },
          )}
          {...register('terms')}
          onChange={(e) => {
            setValue('terms', e.target.checked);
            if (!e.target.checked) {
              trigger('terms');
            } else {
              clearErrors('terms');
            }
          }}
          type="checkbox"
          name="checkbox-input"
          id="checkbox-input"
        />
        <label
          className={cn('text-sm text-footer-gray', {
            'text-error-primary': errors.terms,
          })}
          htmlFor="checkbox-input">
          {dict.newsLetter.terms}{' '}
          <Link href={dict.newsLetter.path}>
            <span className="hover:text-white cursor-pointer text-footer-gray">
              {dict.newsLetter.privacyPolicy}
            </span>
          </Link>
        </label>
      </div>
      {errors.terms && (
        <i className="text-error-primary font-averta-italic text-[13px]">
          {errors.terms.message?.toString()}
        </i>
      )}
    </form>
  );
}
